import { Grid, Stack, TextField, TextFieldProps } from '@mui/material';
import { isCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { CurrencySelect } from '~/components/CurrencySelect/CurrencySelect';
import { SupplierAutocomplete } from '~/components/SupplierAutocomplete';
import { supplierAutocompleteHelper } from '~/components/SupplierAutocomplete/SupplierAutocomplete.helper';

const commonInputProps: Partial<TextFieldProps> = {
  margin: 'none',
  fullWidth: true,
  size: 'small',
  variant: 'outlined',
};

type QuoteDetailsFormProps = {
  disabled?: boolean;
};
export const QuoteDetailsForm = ({ disabled }: QuoteDetailsFormProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'QuoteDetailsForm' });
  const { mapContact, mapLocation } = useMemo(() => supplierAutocompleteHelper(), []);

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <Stack spacing={2}>
          <Controller
            name="quoteReference"
            disabled={disabled}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label={t('quoteReference')}
                  error={!!fieldState.error}
                  {...commonInputProps}
                />
              );
            }}
          />

          <Controller
            name="supplier"
            disabled={disabled}
            render={({ field, fieldState: { error } }) => {
              field.value = field.value || { internalId: null };
              const { internalId } = field.value;
              return (
                <SupplierAutocomplete
                  {...field}
                  disabled={field.disabled}
                  value={internalId || null}
                  size={commonInputProps.size}
                  disableClearable={false}
                  onChange={(value) => {
                    if (value && isCuriosityTranslation(value)) {
                      field.onChange({
                        internalId: value.key,
                        name: value.value,
                        externalReference: value.entity.sourceReference,
                        location: mapLocation(value.entity.locations),
                        contact: mapContact(value.entity.contacts),
                      });
                    } else {
                      field.onChange({
                        location: null,
                        contact: null,
                      });
                    }
                  }}
                  TextFieldProps={{
                    label: t('supplierSearch'),
                    error: !!error,
                  }}
                />
              );
            }}
          />

          <Controller
            name="currency"
            defaultValue=""
            disabled={disabled}
            render={({ field, fieldState: { error } }) => (
              <CurrencySelect {...field} value={field.value || null} error={!!error} {...commonInputProps} />
            )}
          />
        </Stack>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 8,
        }}
      >
        <Controller
          name="summary"
          disabled={disabled}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextField
                label={t('summary')}
                error={!!error}
                multiline
                rows={3}
                {...commonInputProps}
                {...field}
                value={field.value || ''}
              />
            );
          }}
        />
      </Grid>
    </>
  );
};
